// ============================================================================
// MarketFeedCard — карточка заявки в ленте мастера (мобильный вид).
//
// Данные — MarketFeedItem (серверные реквизиты mkt_requests). Контакты до
// оплаченного взятия маскирует СЕРВЕР: карточка показывает состояние
// contact_revealed и не пытается «угадать» телефон/адрес. Кнопка взятия
// вызывает серверную операцию (списание с личного счёта) и показывает явные
// состояния: уже взята / нет денег / ошибка / нет связи.
// ============================================================================

import {
  AlertTriangle,
  BellRing,
  CalendarDays,
  Lock,
  MapPin,
  MessageSquare,
  Phone,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import type { MarketFeedItem, TakeResult } from '@/lib/marketFeed';
import { isTakeFailure } from '@/lib/marketFeed';
import { isMarketPhoneBlocked } from '@/lib/market';
import { marketGeoDistance } from '@/lib/marketGeo';
import { MarketPhoneStatus } from './MarketPhoneStatus';
import { MarketStatusChip } from './MarketStatusChip';
import { MarketRatingBadge } from './MarketRatingBadge';

interface MarketFeedCardProps {
  item: MarketFeedItem;
  busy: boolean;
  /** Ошибка последней попытки взятия именно этой заявки. */
  failure: TakeResult | null;
  /** Валюта личного счёта (символ/код) — для подписи цены. */
  currency?: string;
  /** Взятие недоступно целиком (нет связи / биллинг выключен). */
  takeBlockedReason?: string;
  /** Заявка пришла живьём и ещё не просмотрена — подсветка «новая». */
  highlight?: boolean;
  /** Центр отбора (координаты мастера) — для подписи расстояния. */
  origin?: { lat: number | null; lon: number | null } | null;
  onTake: () => void;
  onOpenChat: () => void;
}

/** Цена взятия: число с группировкой; пустое/нечисловое — как есть, без «0». */
function takePriceLabel(item: MarketFeedItem, currency?: string): string {
  if (item.takePriceValue !== null) {
    const amount = formatValue(item.takePriceValue, 'number:0;group', 'numeric');
    return currency ? `${amount} ${currency}` : amount;
  }
  const raw = item.takePrice.trim();
  if (raw) return currency ? `${raw} ${currency}` : raw;
  return '';
}

function occupiedLabel(item: MarketFeedItem): string {
  if (item.isMine) return 'Взята вами';
  switch (item.status) {
    case 'taken':
      return 'Взята другим мастером';
    case 'in_progress':
      return 'В работе у другого мастера';
    case 'done':
      return 'Выполнена';
    case 'canceled':
      return 'Отменена';
    default:
      return 'Недоступна';
  }
}

function failureText(failure: TakeResult): string {
  if (!failure || !isTakeFailure(failure)) return '';
  switch (failure.kind) {
    case 'no-money':
      return `${failure.message}. Пополните личный счёт и повторите.`;
    case 'already-taken':
      return 'Заявку уже взял другой мастер. Обновите ленту.';
    case 'phone-unverified':
      return `${failure.message} Заказчик должен подтвердить номер через Телеграм/MAX или оператора.`;
    case 'offline':
      return 'Нет связи с сервером — взятие заявки (списание) не выполнено.';
    case 'billing-disabled':
      return `Взятие недоступно: ${failure.message}`;
    case 'permission':
      return `Недостаточно прав: ${failure.message}`;
    default:
      return failure.message;
  }
}

export function MarketFeedCard({
  item,
  busy,
  failure,
  currency,
  takeBlockedReason,
  highlight = false,
  origin = null,
  onTake,
  onOpenChat,
}: MarketFeedCardProps) {
  const created = formatValue(item.createdAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
  const price = takePriceLabel(item, currency);
  const showTakeButton = !item.isMine && !item.isTakenByOther && item.status === 'new';
  const phoneBlocked = isMarketPhoneBlocked(item) && !item.contactRevealed;
  // Гео-подпись: город/район не маскируются (ссылки справочников); расстояние —
  // только когда координаты заявки известны (раскрыты после взятия).
  const location = [item.cityName, item.areaName].filter(Boolean).join(', ');
  const distanceKm = marketGeoDistance(origin, item.geo);
  const distanceLabel = distanceKm === null
    ? ''
    : `${distanceKm < 1 ? '<1' : distanceKm.toFixed(1)} км`;
  const failureIsPhone = !!failure && isTakeFailure(failure) && failure.kind === 'phone-unverified';
  // Один блок статуса на карточку: не дублируем его текстом ошибки/замком.
  const showPhoneNotice = phoneBlocked || failureIsPhone;
  const takeDisabled = !!takeBlockedReason || phoneBlocked || failureIsPhone;

  return (
    <article
      className={`rounded-2xl border bg-card p-4 shadow-sm transition-shadow ${
        highlight ? 'border-primary/50 shadow-primary/10 ring-1 ring-primary/25' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {highlight && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                <BellRing className="h-3 w-3" />
                новая
              </span>
            )}
            {item.categoryName && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                <ShoppingBag className="h-3 w-3" />
                {item.categoryName}
              </span>
            )}
            <MarketStatusChip status={item.status} label={item.statusLabel} />
            {item.isMine && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                <ShieldCheck className="h-3 w-3" />
                Взята вами
              </span>
            )}
            {(item.masterName || item.masterCatalogId) && (
              <MarketRatingBadge
                rating={item.masterRating}
                count={item.masterReviewsCount}
                compact
              />
            )}
          </div>

          <div className="mt-2 line-clamp-3 whitespace-pre-wrap break-words text-sm font-semibold leading-5">
            {item.descr.trim() || 'Описание не указано'}
          </div>
        </div>

        {price && (
          <div className="shrink-0 rounded-xl bg-primary/10 px-2.5 py-1.5 text-right">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Цена взятия</div>
            <div className="text-sm font-semibold tabular-nums text-primary">{price}</div>
          </div>
        )}
      </div>

      <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          <span>{created || 'Дата не указана'}</span>
        </div>

        {(location || distanceLabel) && (
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {location || 'Место работы'}
              {distanceLabel ? ` · ${distanceLabel}` : ''}
            </span>
          </div>
        )}

        {item.contactRevealed ? (
          <>
            {item.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                <a href={`tel:${item.phone}`} className="truncate text-foreground">
                  {item.phone}
                </a>
              </div>
            )}
            {item.address && (
              <div className="flex items-start gap-1.5">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="line-clamp-2">{item.address}</span>
              </div>
            )}
          </>
        ) : showPhoneNotice ? null : (
          <div className="flex items-start gap-1.5">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Контакты скрыты до взятия — телефон и адрес откроются после оплаты
            </span>
          </div>
        )}
      </div>

      {showPhoneNotice && (
        <div className="mt-3">
          <MarketPhoneStatus
            state="unverified"
            audience="master"
            hint={item.phoneVerificationHint || undefined}
            compact
          />
        </div>
      )}

      {item.isTakenByOther && (
        <div className="mt-3 rounded-xl bg-muted/60 px-3 py-2 text-[11px] font-medium text-muted-foreground">
          {occupiedLabel(item)}
        </div>
      )}

      {failure && isTakeFailure(failure)
        && !(failure.kind === 'phone-unverified' && showPhoneNotice) && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11px] leading-5 text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="break-words">{failureText(failure)}</span>
        </div>
      )}

      {takeBlockedReason && showTakeButton && (
        <div className="mt-3 rounded-xl bg-muted/60 px-3 py-2 text-[11px] leading-5 text-muted-foreground">
          {takeBlockedReason}
        </div>
      )}

      {(item.isMine || showTakeButton) && (
        <div className="mt-3 flex gap-2">
          {item.isMine && (
            <Button
              className="h-10 flex-1 rounded-xl"
              onClick={onOpenChat}
            >
              <MessageSquare className="mr-1.5 h-4 w-4" />
              Чат по заявке
            </Button>
          )}
          {showTakeButton && (
            <Button
              className="h-10 flex-1 rounded-xl"
              onClick={onTake}
              disabled={busy || takeDisabled}
            >
              {busy ? (
                <span className="mr-1.5 h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground" />
              ) : null}
              {phoneBlocked || failureIsPhone
                ? 'Взятие недоступно'
                : price ? `Взять за ${price}` : 'Взять заявку'}
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
